<template>
  <div>
    <div class="d-flex flex-wrap align-center mb-3" style="gap: 10px 14px">
      <h2 class="tx-page-title">{{ $t("finance_tab_transactions") }}</h2>
      <v-btn-toggle v-model="view" mandatory dense class="tx-seg">
        <v-btn value="order" small>{{ $t("finance_view_by_transaction") }}</v-btn>
        <v-btn value="statement" small>{{ $t("finance_view_statement") }}</v-btn>
        <v-btn value="refunds" small>
          {{ $t("finance_nav_refunds") }}
          <span v-if="pendingRefunds" class="tx-badge">{{ pendingRefunds }}</span>
        </v-btn>
      </v-btn-toggle>
      <v-spacer />
      <div v-if="view !== 'refunds'" class="tx-filters">
        <finance-period-picker :value="period" @input="$emit('update:period', $event)" />
        <dropdown
          v-model="scope"
          :items="scopeOptions"
          item-text="label"
          item-value="value"
          color="white"
          hide-details
          style="width: 200px"
        />
        <dropdown
          v-if="serviceOptions.length"
          v-model="serviceId"
          :items="serviceOptions"
          item-text="label"
          item-value="value"
          :placeholder="$t('finance_service_label')"
          clearable
          color="white"
          hide-details
          style="width: 150px"
        />
        <div style="width: 210px">
          <text-field
            v-model="search"
            :placeholder="$t('finance_search_transactions')"
            kind="search"
            color="white"
            clearable
            hide-details
          />
        </div>
        <finance-export-menu
          :filename="exportFilename"
          :get-data="getExportData"
          :disabled="view === 'order' ? !visibleGroups.length : !visibleEntries.length"
        />
      </div>
    </div>

    <!-- Lista zahtjeva za refundaciju (GET /refunds) sa statusom -->
    <finance-tab-refunds v-if="view === 'refunds'" @changed="loadPendingRefunds" />

    <!-- Prikaz "Po transakciji": jedna kartica po narudžbi / događaju -->
    <template v-else-if="view === 'order'">
      <div v-if="summary" class="tx-summary mb-3">
        <span>
          {{ $t("finance_summary_orders", { count: summary.orders }) }}
          · {{ $t("finance_summary_payments", { count: paymentCount }) }}
        </span>
        <span>
          {{ $t("finance_turnover") }}
          <strong>{{ num(summary.turnover) }} {{ summary.currency }}</strong>
        </span>
        <span>
          {{ $t("finance_desc_commission") }}
          <strong>{{ num(summary.commission) }}</strong>
        </span>
        <span>
          {{ $t("finance_summary_cash_direct") }} <strong>{{ num(summary.cash) }}</strong>
        </span>
      </div>

      <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-3" />

      <empty-state
        v-if="!loading && !visibleGroups.length"
        icon="mdi-swap-horizontal"
        :title="$t('finance_no_transactions_filtered')"
      />

      <v-card
        v-for="group in visibleGroups"
        :key="group.key"
        outlined
        class="tx-card mb-3"
        :style="{ borderLeft: `5px solid ${group.color}` }"
      >
        <div class="d-flex flex-wrap align-center px-5 pt-4" style="gap: 6px 12px">
          <template v-if="group.isOrder">
            <a class="tx-order-id" @click="openOrder(group.sourceId)"
              >#{{ group.sourceId }}</a
            >
            <span class="tx-date">{{ localeDateTime(group.date) }}</span>
          </template>
          <template v-else>
            <span class="tx-event-title">{{ group.title }}</span>
            <span class="tx-date">{{ localeDate(group.date) }}</span>
          </template>
          <span
            v-for="chip in group.chips"
            :key="chip.text"
            class="tx-chip"
            :class="`tx-chip-${chip.tone}`"
          >
            {{ translate(chip.text) }}
          </span>
          <v-spacer />
          <span class="tx-net-label">{{ group.caption }}</span>
          <span class="tx-net" :style="{ color: group.netColor }">{{
            num(group.net, true)
          }}</span>
        </div>

        <div class="d-flex flex-wrap align-center px-5 pb-4 pt-2" style="gap: 6px 22px">
          <span v-for="line in group.lines" :key="line.id" class="tx-line">
            {{ line.label }}
            <strong :style="{ color: line.color }">{{
              num(line.signed, line.signedDisplay)
            }}</strong>
            <v-tooltip v-if="line.isCash" top max-width="340">
              <template #activator="{ on, attrs }">
                <v-icon x-small v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
              </template>
              <span>{{ $t("finance_cash_line_tooltip") }}</span>
            </v-tooltip>
          </span>
          <v-spacer />
          <span v-if="group.isOrder" class="tx-actions">
            <a @click="openOrder(group.sourceId)">{{ $t("finance_order_details") }}</a>
            <template v-if="!group.hasRefund">
              ·
              <a @click="openRefund(group.sourceId)">{{
                $t("finance_request_refund")
              }}</a>
            </template>
          </span>
        </div>
      </v-card>

      <div
        class="d-flex flex-wrap align-center justify-space-between py-2"
        style="gap: 8px"
      >
        <span v-if="lastPage > 1" class="text-caption grey--text">{{
          $t("finance_summary_page_note")
        }}</span>
        <v-pagination
          v-if="lastPage > 1"
          :value="page"
          :length="lastPage"
          :total-visible="7"
          @input="load"
        />
      </div>
    </template>

    <!-- Prikaz "Izvod": isti podaci, kumulativno - balance po redu -->
    <v-card v-else outlined>
      <v-data-table
        :headers="statementHeaders"
        :items="visibleEntries"
        :loading="loading"
        :loading-text="$t('finance_loading')"
        :no-data-text="$t('finance_no_transactions_filtered')"
        mobile-breakpoint="0"
        hide-default-footer
        disable-pagination
        dense
      >
        <template #item.date="{ item }">{{
          item.isOpening ? "" : localeDate(item.date)
        }}</template>
        <template #item.description="{ item }">
          <span :class="{ 'font-weight-bold': item.isOpening }">{{
            translate(item.description)
          }}</span>
        </template>
        <template #item.moneyFlow="{ item }">
          <span v-if="flowMeta(item.moneyFlow)" class="d-flex align-center">
            <span
              class="tx-dot mr-2"
              :style="{ background: flowMeta(item.moneyFlow).color }"
            />
            {{ $t(flowMeta(item.moneyFlow).labelKey) }}
          </span>
        </template>
        <template #item.amount="{ item }">
          <span
            v-if="!item.isOpening"
            :style="{ color: netColor(item.amount), fontWeight: 600 }"
          >
            {{ num(item.amount, true) }}
          </span>
        </template>
        <template #item.balance="{ item }">
          <span class="font-weight-bold">{{ num(item.balance) }}</span>
        </template>
      </v-data-table>
    </v-card>

    <finance-order-detail-dialog v-model="orderOpen" :order-id="orderId" />
    <refund-form-dialog
      v-model="refundOpen"
      :preset-order-id="refundOrderId"
      :saving="refundSaving"
      @save="onSaveRefund"
    />
  </div>
</template>

<script>
import EmptyState from "@/components/global/EmptyState.vue";
import hasSnackbar from "@/mixins/hasSnackbar";
import restaurantFinanceService from "@/services/restaurantFinance";
import FinanceExportMenu from "./FinanceExportMenu.vue";
import FinanceOrderDetailDialog from "./FinanceOrderDetailDialog.vue";
import FinancePeriodPicker from "./FinancePeriodPicker.vue";
import FinanceTabRefunds from "./FinanceTabRefunds.vue";
import RefundFormDialog from "./dialogs/RefundFormDialog.vue";
import hasFinanceContext from "./hasFinanceContext";
import { parseOverview } from "./overview";
import {
  EVENT_SOURCE_META,
  FLOW_COLORS,
  MONEY_FLOW_META,
  TRANSACTION_TYPE_LABEL_KEYS,
  formatMoney,
} from "./constants";

const PAYMENT_SOURCES = ["RESTAURANT_PAYOUT", "PAYOUT", "PAYMENT_RECEIVED"];
const PAGE_SIZE = 100;

// 1 mjesečno, 2 procenat, 3 po obroku, 4 fiksno - backend odgovor 16.09.2026 §3.1.
const RATE_SUFFIX = { 1: "/mj", 2: "%", 3: "/obrok", 4: "" };

const isRefundEntry = (entryType) => /REFUND/.test(entryType || "");

const mapDto = (dto) => ({
  id: dto.id,
  transactionId: dto.transaction_id ?? null,
  entryType: dto.entry_type,
  direction: dto.direction,
  amount: Math.abs(Number(dto.amount) || 0),
  moneyFlow: dto.money_flow || null,
  sourceType: dto.source_type || "",
  sourceId: dto.source_id ?? null,
  rateType: dto.rate_type ?? null,
  rateValue: dto.rate_value != null ? Number(dto.rate_value) : null,
  service: dto.service_name || dto.service || "",
  paymentMethod: dto.payment_method || "",
  note: dto.note || "",
  date: dto.created_at,
});

const splitDescription = (raw) => {
  const index = raw.indexOf(" - ");
  return index === -1
    ? { code: raw, detail: "" }
    : { code: raw.slice(0, index), detail: raw.slice(index + 3) };
};

export default {
  name: "FinanceScreenTransactions",

  components: {
    EmptyState,
    FinanceExportMenu,
    FinanceOrderDetailDialog,
    FinancePeriodPicker,
    FinanceTabRefunds,
    RefundFormDialog,
  },

  mixins: [hasSnackbar, hasFinanceContext],

  props: {
    period: { type: Object, required: true },
  },

  data: () => ({
    loading: false,
    view: "order",
    scope: "all",
    serviceId: null,
    serviceTypes: [],
    search: "",
    searchTimeout: null,
    rows: [],
    page: 1,
    lastPage: 1,
    entries: [],
    openingBalance: 0,
    summary: null,
    pendingRefunds: 0,
    orderOpen: false,
    orderId: null,
    refundOpen: false,
    refundOrderId: null,
    refundSaving: false,
  }),

  computed: {
    currency() {
      return this.restaurantCurrency;
    },
    scopeOptions() {
      return [
        { value: "all", label: this.$t("finance_scope_all_long") },
        { value: "orders", label: this.$t("finance_scope_orders") },
        { value: "payments", label: this.$t("finance_scope_payments") },
      ];
    },
    serviceOptions() {
      return this.serviceTypes.map((st) => ({
        value: st.id,
        label: st.name || st.label || `#${st.id}`,
      }));
    },
    groups() {
      // ORDER redovi se spajaju po narudžbi (backend ih zna razdvojiti po transaction_id, npr. keš
      // red), a ostali redovi sa istim transaction_id (npr. refundacija) pripadaju toj narudžbi.
      const orderKeyByTx = new Map();
      this.rows.forEach((row) => {
        if (
          row.sourceType === "ORDER" &&
          row.sourceId != null &&
          row.transactionId != null
        ) {
          orderKeyByTx.set(row.transactionId, `order:${row.sourceId}`);
        }
      });
      const map = new Map();
      this.rows.forEach((row) => {
        let key;
        if (row.sourceType === "ORDER" && row.sourceId != null)
          key = `order:${row.sourceId}`;
        else if (row.transactionId != null)
          key = orderKeyByTx.get(row.transactionId) || `t${row.transactionId}`;
        else key = `${row.sourceType}:${row.sourceId}:${row.id}`;
        if (!map.has(key)) {
          const isOrder = key.startsWith("order:");
          map.set(key, {
            key,
            isOrder,
            sourceType: isOrder ? "ORDER" : row.sourceType,
            sourceId: isOrder ? key.slice(6) : row.sourceId,
            date: row.date,
            rows: [],
          });
        }
        const group = map.get(key);
        group.rows.push(row);
        if (row.date && (!group.date || row.date < group.date)) group.date = row.date;
      });
      return [...map.values()].map((group) => this.decorateGroup(group));
    },
    visibleGroups() {
      if (this.scope === "orders") return this.groups.filter((g) => g.isOrder);
      if (this.scope === "payments")
        return this.groups.filter((g) => PAYMENT_SOURCES.includes(g.sourceType));
      return this.groups;
    },
    paymentCount() {
      return this.groups.filter((g) => PAYMENT_SOURCES.includes(g.sourceType)).length;
    },
    statementHeaders() {
      return [
        { text: this.$t("finance_date"), value: "date", sortable: false },
        { text: this.$t("finance_description"), value: "description", sortable: false },
        { text: this.$t("finance_direction"), value: "moneyFlow", sortable: false },
        {
          text: this.$t("finance_amount"),
          value: "amount",
          align: "end",
          sortable: false,
        },
        {
          text: this.$t("finance_balance"),
          value: "balance",
          align: "end",
          sortable: false,
        },
      ];
    },
    // Izvod: backend balance po redu ako postoji, inače kumulativ od opening_balance.
    statementRows() {
      let running = Number(this.openingBalance) || 0;
      const sorted = [...this.entries].sort(
        (a, b) => String(a.date).localeCompare(String(b.date)) || a.id - b.id
      );
      const rows = sorted.map((entry) => {
        running += Number(entry.amount) || 0;
        const balance = entry.balance != null ? Number(entry.balance) : running;
        return {
          id: entry.id,
          date: entry.date,
          description: this.describeEntry(entry),
          moneyFlow: entry.money_flow || null,
          sourceId: entry.source_id,
          amount: Number(entry.amount) || 0,
          balance,
        };
      });
      return [
        {
          id: "opening",
          isOpening: true,
          description: this.$t("finance_opening_balance"),
          balance: Number(this.openingBalance) || 0,
        },
        ...rows,
      ];
    },
    visibleEntries() {
      const query = (this.search || "").trim().toLowerCase();
      if (!query) return this.statementRows;
      return this.statementRows.filter(
        (r) =>
          r.isOpening ||
          `${r.description} ${r.sourceId ?? ""}`.toLowerCase().includes(query)
      );
    },
    exportFilename() {
      return `transakcije_${this.period.from}_${this.period.to}`;
    },
  },

  watch: {
    "period.from": "reload",
    "period.to": "reload",
    serviceId: "reload",
    view: "reload",
    restaurantId: "reload",
    scope() {
      // "Narudžbe/Plaćanja" filtriraju učitanu stranicu - nema novog zahtjeva
    },
    search() {
      clearTimeout(this.searchTimeout);
      // Prikaz "Izvod" pretražuje klijentski; "Po transakciji" ide na backend (?search=)
      if (this.view === "order") this.searchTimeout = setTimeout(() => this.load(1), 400);
    },
  },

  created() {
    this.loadServiceTypes();
    this.loadPendingRefunds();
    this.reload();
  },

  beforeDestroy() {
    clearTimeout(this.searchTimeout);
  },

  methods: {
    // Brojevi na karticama su bez valute (valuta je u zbiru iznad) - kao na mockupu.
    num(value, signed) {
      return formatMoney(value, "", { signed });
    },
    flowMeta(value) {
      return value ? MONEY_FLOW_META[value] || null : null;
    },
    netColor(value) {
      if (Math.abs(value) < 0.005) return FLOW_COLORS.cash;
      return value > 0 ? FLOW_COLORS.positive : FLOW_COLORS.negative;
    },
    localeDateTime(value) {
      if (!value) return "";
      return `${this.localeDate(value)} ${new Date(value).toLocaleTimeString("sr-RS", {
        hour: "2-digit",
        minute: "2-digit",
      })}`;
    },

    lineLabel(row) {
      if (isRefundEntry(row.entryType)) return this.$t("finance_event_refund");
      switch (row.entryType) {
        case "ORDER_REVENUE":
          return this.$t("finance_line_value");
        case "COMMISSION": {
          if (row.rateValue == null) return this.$t("finance_desc_commission");
          const suffix = RATE_SUFFIX[row.rateType] ?? "";
          const rate =
            row.rateType === 2
              ? `${row.rateValue} ${suffix}`
              : `${formatMoney(row.rateValue, "")}${suffix}`;
          return `${this.$t("finance_desc_commission")} ${rate}`;
        }
        case "CASH_ADVANCE":
          return this.$t("finance_line_courier_paid");
        case "CASH_RECEIVED":
          return this.$t("finance_line_customer_paid");
        case "DELIVERY_FEE":
          return this.$t("finance_line_delivery_fee");
        default: {
          const key = TRANSACTION_TYPE_LABEL_KEYS[row.entryType];
          return key
            ? this.$t(key)
            : (row.entryType || "").toLowerCase().replace(/_/g, " ");
        }
      }
    },

    // "Neto" nema u backendu: CREDIT - DEBIT svih redova kartice.
    decorateGroup(group) {
      const lines = group.rows.map((row) => {
        const signed = row.direction === "DEBIT" ? -row.amount : row.amount;
        const isCash =
          row.entryType === "CASH_ADVANCE" || row.entryType === "CASH_RECEIVED";
        const isRevenue = row.entryType === "ORDER_REVENUE";
        return {
          id: row.id,
          label: this.lineLabel(row),
          signed,
          // Vrijednost narudžbe se prikazuje bez predznaka, odbici sa minusom
          signedDisplay: !isRevenue,
          isCash,
          color: isRevenue
            ? "rgba(0,0,0,0.87)"
            : isCash
            ? FLOW_COLORS.cash
            : signed >= 0
            ? FLOW_COLORS.positive
            : FLOW_COLORS.negative,
        };
      });
      const net = lines.reduce((sum, line) => sum + line.signed, 0);
      const event = EVENT_SOURCE_META[group.sourceType];
      const hasCash = group.rows.some(
        (r) => r.entryType === "CASH_ADVANCE" || r.entryType === "CASH_RECEIVED"
      );
      const hasRefund = group.rows.some((r) => isRefundEntry(r.entryType));
      const base = { ...group, lines, net, hasRefund };

      if (group.isOrder) {
        const chips = [];
        const service = group.rows.find((r) => r.service)?.service;
        if (service) chips.push({ text: service, tone: "plain" });
        if (hasCash) {
          const advance = group.rows.some((r) => r.entryType === "CASH_ADVANCE");
          chips.push({
            text: `${this.$t("finance_payment_cash")} · ${
              advance
                ? this.$t("finance_cash_model_courier_pays")
                : this.$t("finance_line_customer_paid").toLowerCase()
            }`,
            tone: "plain",
          });
        } else if (group.rows.some((r) => r.paymentMethod === "card")) {
          chips.push({ text: this.$t("finance_payment_card"), tone: "plain" });
        }
        if (hasRefund)
          chips.push({ text: this.$t("finance_chip_refund_approved"), tone: "blue" });
        return {
          ...base,
          chips,
          caption: this.$t("finance_net_for_you"),
          netColor: hasRefund || net === 0 ? "rgba(0,0,0,0.87)" : this.netColor(net),
          color: hasRefund
            ? FLOW_COLORS.correction
            : hasCash
            ? FLOW_COLORS.cash
            : FLOW_COLORS.positive,
        };
      }

      const note = group.rows.find((r) => r.note)?.note;
      return {
        ...base,
        chips: note ? [{ text: note, tone: "plain" }] : [],
        title: event
          ? this.$t(event.labelKey)
          : (group.sourceType || "").toLowerCase().replace(/_/g, " ") || "—",
        caption: event ? this.$t(event.captionKey) : "",
        netColor: event && event.color === "#1d1d1f" ? event.color : this.netColor(net),
        color: event ? event.color : FLOW_COLORS.cash,
      };
    },

    describeEntry(entry) {
      const { code, detail } = splitDescription(entry.description || "");
      if (detail) return detail;
      const key = TRANSACTION_TYPE_LABEL_KEYS[code];
      return key ? this.$t(key) : entry.description || "";
    },

    reload() {
      if (this.view === "refunds") return;
      this.loadSummary();
      return this.view === "order" ? this.load(1) : this.loadStatement();
    },

    async loadServiceTypes() {
      try {
        const { data } = await restaurantFinanceService.getServiceTypes();
        const list = Array.isArray(data) ? data : data?.data || [];
        this.serviceTypes = list.filter((st) => st && st.id != null);
      } catch (error) {
        this.serviceTypes = [];
      }
    },

    // Broj zahtjeva za refundaciju na čekanju - značka na prekidaču.
    async loadPendingRefunds() {
      if (!this.restaurantId) return;
      try {
        const { data } = await restaurantFinanceService.getRefunds(this.restaurantId);
        this.pendingRefunds = (data?.data || []).filter(
          (r) => r.status === "pending"
        ).length;
      } catch (error) {
        this.pendingRefunds = 0;
      }
    },

    // Zbir iznad liste (promet, provizija, keš direktno, broj narudžbi) iz razrade za isti period.
    async loadSummary() {
      if (!this.restaurantId) return;
      try {
        const res = await restaurantFinanceService.getFinancialOverview(
          this.restaurantId,
          {
            from: this.period.from,
            to: this.period.to,
          }
        );
        const overview = parseOverview(
          res.data?.data || res.data || {},
          this.restaurantCurrency
        );
        const orders = Number(
          (overview.kpis.find((k) => k.key === "order_count") || {}).value
        );
        this.summary = {
          currency: overview.currency,
          orders: Number.isFinite(orders)
            ? orders
            : this.groups.filter((g) => g.isOrder).length,
          turnover: overview.turnover ?? 0,
          commission: Math.abs(
            (overview.rows.find((r) => r.key === "commission") || {}).value || 0
          ),
          cash: Math.abs(
            overview.rows.filter((r) => r.isCash).reduce((sum, r) => sum + r.value, 0)
          ),
        };
      } catch (error) {
        this.summary = null;
      }
    },

    async load(page = 1) {
      if (!this.restaurantId) return;
      this.loading = true;
      try {
        const { data } = await restaurantFinanceService.getTransactions(
          this.restaurantId,
          {
            from: this.period.from,
            to: this.period.to,
            service_id: this.serviceId || undefined,
            search: this.search || undefined,
            per_page: PAGE_SIZE,
            page,
          }
        );
        this.rows = (data?.data || []).map(mapDto);
        this.page = data?.meta?.current_page || 1;
        this.lastPage = data?.meta?.last_page || 1;
      } catch (error) {
        this.showErrorSnackbar(this.$t("finance_transactions_load_error"));
      } finally {
        this.loading = false;
      }
    },

    async loadStatement() {
      if (!this.restaurantId) return;
      this.loading = true;
      try {
        const { data } = await restaurantFinanceService.getBalanceLedger(
          this.restaurantId,
          {
            from: this.period.from,
            to: this.period.to,
          }
        );
        const payload = data?.data || {};
        this.openingBalance = payload.opening_balance || 0;
        this.entries = payload.ledger_entries || [];
      } catch (error) {
        this.showErrorSnackbar(this.$t("finance_balance_ledger_load_error"));
      } finally {
        this.loading = false;
      }
    },

    openOrder(id) {
      this.orderId = id;
      this.orderOpen = true;
    },

    openRefund(orderId) {
      this.refundOrderId = orderId;
      this.refundOpen = true;
    },

    async onSaveRefund(payload) {
      this.refundSaving = true;
      try {
        await restaurantFinanceService.createRefund(this.restaurantId, payload);
        this.showSuccessSnackbar(this.$t("finance_refund_requested"));
        this.refundOpen = false;
        this.loadPendingRefunds();
      } catch (error) {
        this.showErrorSnackbar(
          error?.response?.data?.message || this.$t("finance_refund_request_error")
        );
      } finally {
        this.refundSaving = false;
      }
    },

    getExportData() {
      if (this.view === "statement") {
        return {
          headers: [
            this.$t("finance_date"),
            this.$t("finance_description"),
            `${this.$t("finance_amount")} (${this.currency})`,
            this.$t("finance_balance"),
          ],
          rows: this.visibleEntries.map((r) => [
            r.isOpening ? "" : r.date,
            r.description,
            r.amount ?? "",
            r.balance,
          ]),
        };
      }
      return {
        headers: [
          this.$t("finance_order"),
          this.$t("finance_date"),
          this.$t("finance_description"),
          `${this.$t("finance_net")} (${this.currency})`,
        ],
        rows: this.visibleGroups.map((g) => [
          g.isOrder ? `#${g.sourceId}` : g.title,
          g.date,
          g.lines.map((l) => `${l.label} ${l.signed.toFixed(2)}`).join(" | "),
          g.net.toFixed(2),
        ]),
      };
    },
  },
};
</script>

<style scoped>
.tx-page-title {
  font-size: 1.6rem;
  font-weight: 800;
  margin: 0;
}

.tx-seg {
  border: 1px solid #d9dbe0;
  border-radius: 6px;
  background: #fff;
}

/* Filteri stoje u jednom redu; kad ne stanu, cijela grupa prelazi u novi red */
.tx-filters {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 10px;
}

.tx-seg,
.tx-seg .v-btn {
  height: 48px !important;
}

.tx-seg .v-btn {
  text-transform: none;
  letter-spacing: 0;
  font-weight: 700;
  background: transparent !important;
}

.tx-seg .v-btn--active {
  background: #1d1d1f !important;
  color: #fff !important;
}

.tx-badge {
  margin-left: 6px;
  background: #fdf0d5;
  color: #8a5a00;
  border-radius: 999px;
  padding: 0 7px;
  font-size: 0.72rem;
}

.tx-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 28px;
  font-size: 0.88rem;
  color: rgba(0, 0, 0, 0.6);
}

.tx-summary strong {
  color: rgba(0, 0, 0, 0.87);
}

.tx-order-id {
  font-weight: 800;
  font-size: 1.02rem;
  text-decoration: underline;
}

.tx-event-title {
  font-weight: 800;
  font-size: 1.02rem;
}

.tx-date {
  font-size: 0.86rem;
  color: rgba(0, 0, 0, 0.6);
}

.tx-chip {
  border-radius: 6px;
  padding: 2px 10px;
  font-size: 0.8rem;
  background: #f1f1ef;
}

.tx-chip-blue {
  background: #e4edfc;
  color: #1c4fb5;
}

.tx-net-label {
  font-size: 0.78rem;
  color: rgba(0, 0, 0, 0.55);
}

.tx-net {
  font-size: 1.35rem;
  font-weight: 800;
}

.tx-line {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.88rem;
}

.tx-actions {
  font-size: 0.85rem;
}

.tx-actions a {
  text-decoration: underline;
  cursor: pointer;
}

.tx-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
  flex: 0 0 auto;
}
</style>
